import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  FileText,
  ShieldCheck,
  Fuel,
  Ban,
  Scale,
  Clock,
  Car,
  Trash2,
  Globe,
  School,
  Luggage,
  Key,
  Gauge,
  Tag,
  CarFront,
  CarIcon,
  Brush,
  Shield,
  Hourglass,
  Rotate3D,
  Eclipse,
  IdCard,
} from "lucide-react";

const sections = [
  {
    id: "security",
    icon: <ShieldCheck size={18} className="text-blue-500" />,
    title: "Security Deposit",
    content: (
      <p>
        A security deposit ranging from <strong>Rs. 1,500 to Rs. 8,000</strong> will be charged depending on your choice of vehicle.{" "}
        <strong>This deposit will be refunded in 10 bank working days from the vehicle drop-off time,</strong>{" "}
        following the verification of FASTAG usage and any traffic violations. Please note that traffic violations typically reflect on the government portal 3 to 4 days after the booking ends.
      </p>
    ),
  },
  {
    id: "age",
    icon: <IdCard size={18} className="text-purple-500" />,
    title: "Age & ID Verification",
    content: (
      <div className="space-y-2">
        <p><strong>Age Criteria:</strong> The minimum age requirement to rent a vehicle from Wow Carz is 21 years.</p>
        <p><strong>ID Verification:</strong> Your driving license must be 1 year old as of the rental start date. During vehicle pick-up, the primary renter must show a physical copy of their original driver's license. Our executive will physically verify the license and collect one original ID document for submission.</p>
        <p>Failure to provide these documents will result in our inability to hand over the vehicle. In such cases, the booking will be treated as a late cancellation, and a <strong>100% cancellation fee</strong> will apply.</p>
      </div>
    ),
  },
  {
    id: "kyc",
    icon: <FileText size={18} className="text-orange-500" />,
    title: "Mandatory KYC Documents",
    content: (
      <div className="space-y-2">
        <p>You must provide all of the following:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Driver's License</li>
          <li>Aadhaar Card</li>
          <li>Employee ID or Student ID or GST Certificate</li>
          <li><strong>Local Residents:</strong> Valid Rental Agreement, Latest Gas Bill, Valid PG Bill, or Valid Passport</li>
          <li><strong>Non-Local Residents:</strong> Travel Tickets (Arrival and departure) + Hotel Reservation/Accommodation Booking</li>
        </ul>
        <p className="text-sm text-gray-500">Note: The person who booked the car should be present for vehicle pickup and drop off.</p>
      </div>
    ),
  },
  {
    id: "package",
    icon: <Scale size={18} className="text-green-500" />,
    title: "KM Package Plan",
    content: (
      <div className="space-y-2">
        <p>The KM package plan cannot be modified once the booking is confirmed.</p>
        <p><strong>Extra Kilometre Charges:</strong> We offer multiple kilometre packages tailored to your needs, each with its own specific distance limit. Your total kilometre allowance is calculated based on your booking duration by the hourly limit of your selected plan.</p>
        <p>If you exceed this total limit, you will be charged an <strong>"extra km fee"</strong> for the additional distance driven. These rates vary depending on the car model and the city.</p>
      </div>
    ),
  },
  {
    id: "fuel",
    icon: <Fuel size={18} className="text-red-500" />,
    title: "Fuel Reimbursement",
    content: (
      <div className="space-y-2 text-sm">
        <p>All kilometre packages are prepared excluding fuel. We request you to return the car with the same fuel level as provided.</p>
        <p>To qualify for a fuel refund for excess fuel returned, the fuel level must be at least <strong>20% higher</strong> than it was at the start of the trip. You must submit a valid printed fuel bill featuring the vehicle's registration number at the time of drop-off.</p>
        <p>The date on the fuel bill must fall within your booking period, and hand-written bills will not be accepted.</p>
      </div>
    ),
  },
  {
    id: "ev vehicle return policy",
    icon: <Fuel size={18} className="text-green-500" />,
    title: "EV Vehicle Return Policy",
    content: (
      <div className="space-y-2 text-sm">
        <p>Vehicle will be provided with 85% to 100% Charge. Customer has to Return at 85% – 100%: ₹0 (No
          Convenience Charge), Return at 50% – 84%: ₹300 (Flat Convenience Charge), Return at 20% – 49%:
          ₹600 (Flat Convenience Charge), Return below 20%: ₹1,000 (Flat Charge for Fast-Charging) Note: Using
          vehicle less than 30% charge is not advisable - Immediate charging is recommended.</p>
      </div>
    ),
  },
  {
    id: "ev charging & running repair",
    icon: <Fuel size={18} className="text-green-500" />,
    title: "EV Charging & Running Repair",
    content: (
      <div className="space-y-2 text-sm">
        <p>The customer should pay the charging cost during the trip duration. Customers must use compatible
          public charging stations & approved charging equipment suitable for EV vehicles.</p><p> WOWCARZ-EV cannot
            assume responsibility for unforeseen electrical malfunctions. In instances where the vehicle is operated
            with hard acceleration, rash driving method, driving through flooded water, the customer will be
            responsible for bearing the charges for acceleration pad wear/repair as prescribed by our fleet
            executive.</p> <p> Customers must not use damaged cables, incompatible connectors, unauthorized adapters
              or unsafe electrical connections. Any Damage to charging cables, connectors, charging ports or
              supplied accessories, Infotainment system may be recovered during drop off time as repair cost along
              with downtime charges.</p>
      </div>
    ),
  },
  {
    id: "cancel",
    icon: <Ban size={18} className="text-red-600" />,
    title: "Cancellation and Charges",
    content: (
      <div className="space-y-2">
        <p>Customer should inform <strong>24 hours prior</strong> to the booking start time. In such instances, <strong>70% of the amount will be refunded</strong> (30% deduction).</p>
        <p>If failed to inform 24 hours prior, there will be a <strong>100% deduction with no refund.</strong></p>
        <p className="text-sm text-gray-500">Note: Any booking created within 24 hours of the booking start time will not be eligible for cancellation — no refund policy applies.</p>
      </div>
    ),
  },
  {
    id: "rescheduling",
    icon: <Eclipse size={18} className="text-yellow-600" />,
    title: "Rescheduling Rules",
    content: (
      <ul className="list-disc ml-5 space-y-1 text-sm">
        <li>Must be informed 24 hours prior to booking start time.</li>
        <li>Cannot prepone the start time within 6 hours of the booking start time.</li>
        <li>Duration should be the same or more than the current booking duration.</li>
        <li>The kilometre package cannot be changed.</li>
        <li>Tariffs might differ based on weekday and weekend charges.</li>
        <li>Rescheduling with the same vehicle is subject to availability. Change of vehicle may incur additional charges.</li>
      </ul>
    ),
  },
  {
    id: "extension",
    icon: <Hourglass size={18} className="text-red-700" />,
    title: "Extension",
    content: (
      <div className="space-y-2 text-sm">
        <p>Customer should inform a minimum of <strong>6 hours</strong> prior to drop-off time for an extension. Minimum extension duration is 6 hours.</p>
        <p>Buffer time: 1 hour for vehicle drop off.</p>
      </div>
    ),
  },
  {
    id: "extrahour",
    icon: <Rotate3D size={18} className="text-teal-600" />,
    title: "Extra Hour Charges",
    content: (
      <ul className="list-disc ml-5 space-y-1 text-sm">
        <li><strong>₹250/hour</strong> — if informed 6+ hours before drop.</li>
        <li><strong>₹400/hour</strong> — if not informed or informed within 6 hours of drop.</li>
        <li>Customer should share a complete video of the car if extending more than two times.</li>
      </ul>
    ),
  },
  {
    id: "damage",
    icon: <Shield size={18} className="text-blue-700" />,
    title: "Damage & Accident Policy",
    content: (
      <div className="space-y-2 text-sm">
        <p>In occurrence of vehicle damage, the customer shall be solely responsible regardless of cause.</p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Under ₹30,000:</strong> Customer pays repair cost plus downtime losses.</li>
          <li><strong>Over ₹30,000:</strong> Comprehensive insurance claimed; customer pays estimation difference plus downtime losses.</li>
          <li><strong>Total Loss:</strong> Comprehensive insurance claimed; customer pays estimation difference, parking charges, plus 3 months downtime losses.</li>
        </ul>
        <p className="font-semibold mt-2">Negligence & Prohibited Situations (irrespective of insurance):</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Consequential damage (driving while impaired)</li>
          <li>Beach/off-road drive (₹8,000 penalty + damages)</li>
          <li>Under influence of alcohol/narcotics (full cost)</li>
          <li>Different driver than booking person</li>
          <li>Exceeding seating capacity</li>
          <li>Failure to pay inter-state tax</li>
          <li>Rash and negligent driving</li>
          <li>Violation of traffic rules / Motor Vehicles Act</li>
        </ul>
        <p><strong>Towing Charges:</strong> Full cost to be paid by the customer in case of negligence/accident.</p>
      </div>
    ),
  },
  {
    id: "cleanliness",
    icon: <Brush size={18} className="text-red-700" />,
    title: "Cleanliness & Maintenance",
    content: (
      <div className="space-y-2 text-sm">
        <p>Customers must ensure the vehicle is not soiled. Penalty/damages — minimum <strong>₹1,250</strong> — for:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Chewing gum / Tobacco</li>
          <li>Burning any substance (Cigarettes / Incense)</li>
          <li>Visible fabric stains / Torn fabric</li>
          <li>Pets inside vehicle</li>
          <li>Presence of sand / excessive mud</li>
        </ul>
      </div>
    ),
  },
  {
    id: "running",
    icon: <CarIcon size={18} className="text-yellow-700" />,
    title: "Running Repair",
    content: (
      <p className="text-sm">
        Wow Carz cannot assume responsibility for unforeseen electrical malfunctions. In instances where the vehicle is operated using the half-clutch method, the customer will be responsible for bearing the charges for clutch plate wear/repair as prescribed by our fleet executive.
      </p>
    ),
  },
  {
    id: "interstate",
    icon: <CarFront size={18} className="text-orange-700" />,
    title: "Interstate Trip",
    content: (
      <p className="text-sm">
        Customer pays all toll charges, parking charges, and state permit charges. Customer can purchase permits online or at the respective check post.
      </p>
    ),
  },
  {
    id: "fastag",
    icon: <Tag size={18} className="text-teal-700" />,
    title: "FASTAG Recharge",
    content: (
      <p className="text-sm">
        Please recharge your FASTAG via PhonePe or Google Pay using Kotak Mahindra Bank and your Vehicle Number. A screenshot of the recharge receipt must be shared at the time of vehicle drop-off. Using our existing FASTAG balance without recharging will result in a <strong>double penalty charge</strong> for the total amount used.
      </p>
    ),
  },
  {
    id: "overspeeding",
    icon: <Gauge size={18} className="text-blue-700" />,
    title: "Over Speeding",
    content: (
      <p className="text-sm">
        Speed limit: 80–110 km/hr. Exceeding 110 km/hr leads to <strong>₹500 per violation.</strong>
      </p>
    ),
  },
  {
    id: "key",
    icon: <Key size={18} className="text-gray-700" />,
    title: "Key Loss",
    content: (
      <p className="text-sm">
        If the customer loses the key due to their negligence, the charges would be a minimum of <strong>₹15,000</strong>. Charges may increase based on vehicle model.
      </p>
    ),
  },
  {
    id: "personal",
    icon: <Luggage size={18} className="text-yellow-700" />,
    title: "Personal Belongings",
    content: (
      <p className="text-sm">
        Wow Carz will not be held responsible for any damage to, loss or theft of any personal property belonging to users, co-passengers, or third parties regardless of fault or negligence.
      </p>
    ),
  },
  {
    id: "jurisdiction",
    icon: <School size={18} className="text-teal-700" />,
    title: "Jurisdiction",
    content: (
      <p className="text-sm">
        Any proceedings, interim or interlocutory relief, arising from arbitration shall be brought in a court of competent jurisdiction in Bengaluru. Submission to Bengaluru jurisdiction does not limit the customer's right to commence proceedings in any other jurisdiction deemed appropriate.
      </p>
    ),
  },
];

const TermsAndConditions = () => {
  const [activeId, setActiveId] = useState(sections[0].id);
  const sectionRefs = useRef({});
  const observerRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );

    sections.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const activeEl = navRef.current?.querySelector(`[data-id="${activeId}"]`);
    activeEl?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-transparent pt-20 md:pt-24 text-left">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight">
            Terms & Conditions
          </h1>
          <p className="text-gray-500 mt-2 text-sm">
            Please read all clauses carefully to ensure a seamless rental.
          </p>
        </div>

        <div className="flex gap-8 items-start">
          {/* Sticky side nav — desktop only */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pb-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" ref={navRef}>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Sections</p>
            <nav className="space-y-1 pb-28">
              {sections.map(({ id, icon, title }) => (
                <button
                  key={id}
                  data-id={id}
                  onClick={() => scrollTo(id)}
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-all ${activeId === id
                    ? "bg-red-50 text-red-600 font-semibold"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    }`}
                >
                  <span className="flex-shrink-0">{icon}</span>
                  <span className="leading-tight">{title}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 space-y-10">
            {sections.map(({ id, icon, title, content }) => (
              <section
                key={id}
                id={id}
                ref={(el) => (sectionRefs.current[id] = el)}
                className="scroll-mt-28"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                    {icon}
                  </div>
                  <h2 className="text-base md:text-lg font-bold text-gray-800">{title}</h2>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 text-gray-600 leading-relaxed text-sm">
                  {content}
                </div>
              </section>
            ))}

            <p className="text-gray-400 text-xs italic pb-28">
              Wow Carz is not responsible for loss or theft of personal property regardless of fault or negligence.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
